import React from "react";
import { useHistory } from "react-router-dom";
import { Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import * as actions from "@/redux/actions";
import SntImpersonateUserIcon from "@icons/SntImpersonateUserIcon";
import SntSupportUserIcon from "@icons/SntSupportUserIcon";
import SntSupportAdminIcon from "@icons/SntSupportAdminIcon";
import userClient from "@apis/userClient";
import useUser from "@hooks/useUser";
import SntButton from "@cleanup/widgets/forms/SntButton";
import useErrorDialog from "@cleanup/widgets/dialog/useErrorDialog";
import { RootState } from "@/types/RootState";

interface ImpersonateUser {
  supportMode?: boolean;
  adminMode?: boolean;
  hasAdminMode?: boolean;
}

interface ImpersonateHeaderProps {
  impersonateUser?: ImpersonateUser | Record<string, unknown>;
  onLeaveClicked?: () => void;
}

const ImpersonateHeader: React.FC<ImpersonateHeaderProps> = ({
  impersonateUser,
  onLeaveClicked,
}) => {
  const language = useSelector((state: RootState) => state.language);
  const loginInfo = useSelector((state: RootState) => state.user);
  const history = useHistory();

  const { onImpersonateUser, IMPERSONATE_TYPE } = useUser();

  const dispatch = useDispatch();

  const { handleOpen: openErrorDialog, ErrorDialog } = useErrorDialog({
    language,
    title: undefined,
  });

  const handleClick = () => {
    if (onLeaveClicked) {
      onLeaveClicked();
    }

    userClient
      .getPrevUserByTokens()
      .then(() => {
        dispatch(actions.preventSidebar(true));

        const bc = new BroadcastChannel("impersonate_reload");
        bc.postMessage("reload the tabs.");
        bc.close();

        history.push("/");
      })
      .catch(() => {
        history.push("/logout");
      });
  };

  // Type guard to check if impersonateUser has the expected structure
  const isImpersonateUser = (
    user: ImpersonateUser | Record<string, unknown> | undefined
  ): user is ImpersonateUser => {
    return user !== undefined && typeof user === "object";
  };

  const user = isImpersonateUser(impersonateUser) ? impersonateUser : undefined;

  return user && loginInfo ? (
    <div
      style={{
        zIndex: 1003,
        height: 50,
        position: "fixed",
        width: "100%",
        backgroundColor: user.supportMode
          ? "rgb(255 168 88)"
          : "#212851",
        color: user.supportMode ? "#212851" : "white",
      }}
      className="d-flex align-items-center ps-4"
    >
      {user.supportMode ? (
        <>
          <div className="pe-3">
            {user.adminMode ? (
              <SntSupportAdminIcon size={24} />
            ) : (
              <SntSupportUserIcon size={24} />
            )}
          </div>
          <div className="pe-3">
            <span className="fw-bold">{language.support_mode_key}</span>
            {" | "}
            <span>
              {user.adminMode ? (
                <>{language.sidebar_admin_key}</>
              ) : (
                <>{language.read_only_key}</>
              )}
            </span>
            {" | "}
            <span
              title={
                !user.hasAdminMode && !user.adminMode
                  ? language.viewer_title_key
                  : ""
              }
              className="pe-3"
            >
              {language.organization_key}: {loginInfo.organisation?.name}
            </span>
            {user.hasAdminMode && !user.adminMode ? (
              <SntButton
                variant="sensolus"
                className="me-3"
                style={{ backgroundColor: "#ffa85800", color: "#212851" }}
                onClick={() => {
                  onImpersonateUser({
                    id: loginInfo.organisation?.id ?? 0,
                    type: IMPERSONATE_TYPE.ADMIN,
                    onBadRequest: () => {
                      openErrorDialog(
                        language.impersonation_insufficient_rights_key
                      );
                    },
                  });
                }}
                icon={<SntSupportAdminIcon />}
                text={language.admin_mode_key}
              />
            ) : (
              <></>
            )}
            <Button
              variant="sensolus"
              style={{ backgroundColor: "#ffa85800", color: "#212851" }}
              onClick={handleClick}
            >
              {language.leave_key}
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="pe-3">
            <SntImpersonateUserIcon size={24} />
          </div>
          <div className="pe-3">
            <span className="fw-bold">{language.impersonation_mode}</span> |{" "}
            <span>
              {language.user_key}: {loginInfo.fullName}
            </span>{" "}
            - <span>{loginInfo.organisation?.name}</span>
          </div>
          <div className="pe-3">
            <Button
              variant="sensolus"
              style={{ borderColor: "white" }}
              className="fw-bold"
              onClick={handleClick}
            >
              {language.leave_key}
            </Button>
          </div>
        </>
      )}
      <ErrorDialog />
    </div>
  ) : (
    <></>
  );
};

export default ImpersonateHeader;
